// EmailModal.jsx
import React, { useEffect, useState, useRef } from "react";
import { Button, Modal, Form, Row, Col, Alert, Card, Nav, Badge } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";
import { createReminder, updateReminder } from "../../afinanceapi";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import "./Comms.css";

function AccountsEmailModal({ emailDetail, practices = [], onSmsUpdate = () => {}, ...props }) {
  const [user, setUser] = useState({});
  const [successAlert, setSuccessAlert] = useState(false);
  const [closeAlert, setCloseAlert] = useState(false);
  const [activeTab, setActiveTab] = useState("statement");

  

  // unified (no per-type colors)
  const emailTypes = {
    statement: { label: "Statement Emails" },
    invoice: { label: "Invoice Emails" },
    receipt: { label: "Receipt Emails" },       // No variables provided → shows none
    creditNote: { label: "Credit Note Emails" },
  };

  const [emailData, setEmailData] = useState({
    statement: { subject: "", text: "", logos: {}, ccSender: false, ccAddress: "" },
    invoice: { subject: "", text: "", logos: {}, ccSender: false, ccAddress: "" },
    receipt: { subject: "", text: "", logos: {}, ccSender: false, ccAddress: "" },
    creditNote: { subject: "", text: "", logos: {}, ccSender: false, ccAddress: "" },
  });

  const quillRefs = {
    statement: useRef(null),
    invoice: useRef(null),
    receipt: useRef(null),
    creditNote: useRef(null),
  };

  // === NEW: variable sets per email type (uses ${token} format) ===
  // Matches your spec exactly; where you wrote "patientName = first + last", we expose patientName AND the separate parts when applicable.
  const VARIABLE_SETS = {
    statement: [
      { label: "Start Date", value: "${startDate}" },
      { label: "End Date", value: "${endDate}" },
      { label: "Patient Name", value: "${patientName}" }, // patientFirstName + patientLastName
      { label: "Practice Name", value: "${practiceName}" },
      { label: "Total (Patient)", value: "${totalPatient}" },
      { label: "Total (Medical Aid)", value: "${totalMedicalAid}" },
      { label: "Practice Phone", value: "${practicePhone}" },
      { label: "Practice Email", value: "${practiceEmail}" },
    ],
    invoice: [
      { label: "Invoice Date", value: "${invoiceDate}" },
      { label: "Invoice Number", value: "${invoiceNumber}" },
      // Your note: patientName = patientFirstName, patientLastName
      { label: "Patient First Name", value: "${patientFirstName}" },
      { label: "Patient Last Name", value: "${patientLastName}" },
      { label: "Patient Name (Full)", value: "${patientName}" }, // convenience
      { label: "Practice Name", value: "${practiceName}" },
      { label: "Practice Number", value: "${practiceNumber}" },
      { label: "Practice Email", value: "${practiceEmail}" },
      { label: "Practice Phone", value: "${practicePhone}" },
    ],
    // No variables provided in your spec → leave empty (no pills will show)
    receipt: [
      { label: "Receipt Date", value: "${receiptDate}" },
      { label: "Receipt Number", value: "${receiptNumber}" },
      { label: "Patient First Name", value: "${patientFirstName}" },
      { label: "Patient Last Name", value: "${patientLastName}" },
      { label: "Practice Name", value: "${practiceName}" },
      { label: "Practice Number", value: "${practiceNumber}" },
      { label: "Practice Email", value: "${practiceEmail}" },
      { label: "Practice Phone", value: "${practicePhone}" },
    ],
    creditNote: [
      { label: "Credit Note Date", value: "${creditNoteDate}" },
      { label: "Credit Note Number", value: "${creditNoteNumber}" },
      { label: "Patient First Name", value: "${patientFirstName}" },
      { label: "Patient Last Name", value: "${patientLastName}" },
      { label: "Practice Name", value: "${practiceName}" },
      { label: "Practice Number", value: "${practiceNumber}" },
      { label: "Practice Email", value: "${practiceEmail}" },
      { label: "Practice Phone", value: "${practicePhone}" },
    ],
  };

  useEffect(() => {
    const token = sessionStorage.getItem("user");
    if (!token) return;
    setUser(jwtDecode(token));
  }, []);

  // Populate state for edit / defaults for create (logos left empty object)
  useEffect(() => {
    if (emailDetail) {
      setEmailData({
              statement: {
         subject: emailDetail.statementSubject || emailDetail.statement?.subject || "",
         text:    emailDetail.statementText    || emailDetail.statement?.text    || "",
         logos:   emailDetail.statementLogos   || emailDetail.statement?.logos   || {},
         ccSender: !!(emailDetail.statementCcSender ?? emailDetail.statement?.ccSender),
         ccAddress: emailDetail.statementCcAddress ?? emailDetail.statement?.ccAddress ?? "",
       },
       invoice: {
         subject: emailDetail.invoiceSubject   || emailDetail.invoice?.subject   || "",
         text:    emailDetail.invoiceText      || emailDetail.invoice?.text      || "",
         logos:   emailDetail.invoiceLogos     || emailDetail.invoice?.logos     || {},
         ccSender: !!(emailDetail.invoiceCcSender ?? emailDetail.invoice?.ccSender),
         ccAddress: emailDetail.invoiceCcAddress ?? emailDetail.invoice?.ccAddress ?? "",
       },
       receipt: {
         subject: emailDetail.receiptSubject   || emailDetail.receipt?.subject   || "",
         text:    emailDetail.receiptText      || emailDetail.receipt?.text      || "",
         logos:   emailDetail.receiptLogos     || emailDetail.receipt?.logos     || {},
         ccSender: !!(emailDetail.receiptCcSender ?? emailDetail.receipt?.ccSender),
         ccAddress: emailDetail.receiptCcAddress ?? emailDetail.receipt?.ccAddress ?? "",
       },
       creditNote: {
         subject: emailDetail.creditNoteSubject|| emailDetail.creditNote?.subject|| "",
         text:    emailDetail.creditNoteText   || emailDetail.creditNote?.text   || "",
         logos:   emailDetail.creditNoteLogos  || emailDetail.creditNote?.logos  || {},
         ccSender: !!(emailDetail.creditNoteCcSender ?? emailDetail.creditNote?.ccSender),
         ccAddress: emailDetail.creditNoteCcAddress ?? emailDetail.creditNote?.ccAddress ?? "",
       },
      });
    } else {
      setEmailData({
        statement: { subject: "", text: "", logos: {}, ccSender: false, ccAddress: "" },
        invoice:   { subject: "", text: "", logos: {}, ccSender: false, ccAddress: "" },
        receipt:   { subject: "", text: "", logos: {}, ccSender: false, ccAddress: "" },
        creditNote:{ subject: "", text: "", logos: {}, ccSender: false, ccAddress: "" },
      });
    }
  }, [emailDetail]);

  const updateEmailField = (type, field, value) => {
    setEmailData((prev) => ({ ...prev, [type]: { ...prev[type], [field]: value } }));
  };

  // Insert helper (used by pills and logo box)
  const insertIntoQuill = (emailType, value) => {
    const quill = quillRefs[emailType].current?.getEditor();
    if (!quill) return;
    quill.focus();
    const range = quill.getSelection();
    const index = range ? range.index : quill.getLength();
    quill.insertText(index, value, "user");
    quill.setSelection(index + value.length, 0);
    setTimeout(() => updateEmailField(emailType, "text", quill.root.innerHTML), 0);
  };

  const handleDropIntoQuill = (e, emailType) => {
    e.preventDefault();
    e.stopPropagation();
    const varText = e.dataTransfer.getData("text/plain");
    if (varText) insertIntoQuill(emailType, varText);
  };
  const handleDragOverQuill = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  };

  const quillModules = {
    toolbar: [
      ["bold", "italic", "underline"],
      [{ align: [] }],
      [{ list: "ordered" }, { list: "bullet" }],
      ["link"],
    ],
  };
  const quillFormats = ["bold", "italic", "underline", "list", "bullet", "align", "link"];

  // Normalize practice numbers from the decoded user
function extractPracticeNumbers(u) {
  // Prefer user.Practice (array), then user.practice, then other common shapes
  const raw =
    (Array.isArray(u?.Practice) && u.Practice) ||
    (Array.isArray(u?.practice) && u.practice) ||
    (Array.isArray(u?.practiceNumbers) && u.practiceNumbers) ||
    (u?.practiceNumber ? [u.practiceNumber] : []);

  // If the array contains objects, try common fields; else keep the primitive value
  const mapped = raw.map((p) => {
    if (p && typeof p === "object") {
      return (
        p.practiceNumber ??
        p.practiceNo ??
        p.practiceId ??
        p.id ??
        p._id ??
        null
      );
    }
    return p ?? null;
  });

  // De-dupe + filter falsy
  return [...new Set(mapped.filter(Boolean))];
}

const normalizeHtml = (html) =>
  String(html)
    .replace(/<p>/g, "<div>")
    .replace(/<\/p>/g, "</div>");


  const handleSubmit = async (e) => {
    e.preventDefault();
    const practiceNumbers = extractPracticeNumbers(user);
    const payload = {
      reminderType: "Email",
      statementSubject: emailData.statement.subject,
      statementText: normalizeHtml(emailData.statement.text),
      statementLogos: emailData.statement.logos,
      statementCcSender: !!emailData.statement.ccSender,
      statementCcAddress: (emailData.statement.ccAddress || "").trim(),
      invoiceSubject: emailData.invoice.subject,
      invoiceText: normalizeHtml(emailData.invoice.text),
      invoiceLogos: emailData.invoice.logos,
      invoiceCcSender: !!emailData.invoice.ccSender,
      invoiceCcAddress: (emailData.invoice.ccAddress || "").trim(),
      receiptSubject: emailData.receipt.subject,
      receiptText: normalizeHtml(emailData.receipt.text),
      receiptLogos: emailData.receipt.logos,
      receiptCcSender: !!emailData.receipt.ccSender,
      receiptCcAddress: (emailData.receipt.ccAddress || "").trim(),
      creditNoteSubject: emailData.creditNote.subject,
      creditNoteText: normalizeHtml(emailData.creditNote.text),
      creditNoteLogos: emailData.creditNote.logos,
      creditNoteCcSender: !!emailData.creditNote.ccSender,
      creditNoteCcAddress: (emailData.creditNote.ccAddress || "").trim(),
      practiceNumbers,
    };

    try {
      if (emailDetail?._id) await updateReminder(emailDetail._id, payload);
      else await createReminder(payload);
      setSuccessAlert(true);
      setTimeout(() => {
        setSuccessAlert(false);
        props.onHide();
        onSmsUpdate();
      }, 1000);
    } catch (error) {
      console.error("Error saving Email templates:", error);
    }
  };

  const handleClose = () => setCloseAlert(true);
  const handleCancelNo = () => setCloseAlert(false);
  const handleCancelYes = () => {
    setCloseAlert(false);
    props.onHide();
  };

  return (
    <Modal {...props} size="xl" centered onHide={handleClose} style={{ maxWidth: "95vw" }} scrollable>
      <Modal.Header closeButton={!closeAlert && !successAlert}>
        <Modal.Title className="text-primary">
          {emailDetail ? "Edit Financial Email Templates" : "Add Financial Email Templates"}
        </Modal.Title>
      </Modal.Header>

      {closeAlert && (
        <Alert variant="info" className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal">
          <span>Are you sure you want to close? Changes will not be saved.</span>
          <div className="ms-auto d-flex">
            <Button variant="outline-primary" className="me-1" onClick={handleCancelYes}>
              <i className="fa fa-check"></i>
            </Button>
            <Button variant="outline-secondary" onClick={handleCancelNo}>
              <i className="fa fa-times"></i>
            </Button>
          </div>
        </Alert>
      )}

      {successAlert && (
        <Alert variant="success" className="w-100 d-flex align-items-center justify-content-between fs-6 fw-normal">
          <span>Financial email templates saved successfully!</span>
        </Alert>
      )}

      <Modal.Body style={{ maxHeight: "75vh", overflowY: "auto" }}>
        {/* Primary tabs */}
        <Nav variant="pills" className="mb-4 comms-tabs">
          {Object.keys(emailTypes).map((type) => (
            <Nav.Item key={type}>
              <Nav.Link active={activeTab === type} onClick={() => setActiveTab(type)}>
                {emailTypes[type].label}
              </Nav.Link>
            </Nav.Item>
          ))}
        </Nav>

        <Form className="mb-3">
          {Object.keys(emailTypes).map((emailType) => (
            <div key={emailType} style={{ display: activeTab === emailType ? "block" : "none" }}>
              <Row>
                <Col lg={12}>
                  <Card className="h-100">
                    <Card.Body>
                      {/* CC sender toggle */}
                      <Form.Group className="mb-3">
                        <Form.Check
                          type="checkbox"
                          id={`ccSender-${emailType}`}
                          label="CC sender (add the logged-in user's email to the CC when sending this email)"
                          checked={!!emailData[emailType].ccSender}
                          onChange={(e) => updateEmailField(emailType, "ccSender", e.target.checked)}
                        />
                      </Form.Group>

                      {/* Configured CC address(es) — independent of the toggle above. */}
                      <Form.Group className="mb-3">
                        <Form.Label className="small mb-1">
                          Additional CC address(es)
                        </Form.Label>
                        <Form.Control
                          type="text"
                          size="sm"
                          id={`ccAddress-${emailType}`}
                          placeholder="billing@example.com; partner@example.com"
                          value={emailData[emailType].ccAddress || ""}
                          onChange={(e) => updateEmailField(emailType, "ccAddress", e.target.value)}
                        />
                        <Form.Text className="text-muted">
                          Optional. Separate multiple addresses with <code>,</code> or <code>;</code>. Combines with the &quot;CC sender&quot; option above.
                        </Form.Text>
                      </Form.Group>

                      {/* Subject */}
                      <Form.Group className="mb-3">
                        <Form.Label>
                          <strong>Email Subject:</strong>
                        </Form.Label>
                        <Form.Control
                          type="text"
                          placeholder={`Enter subject for ${emailTypes[emailType].label.toLowerCase()}`}
                          value={emailData[emailType].subject}
                          onChange={(e) => updateEmailField(emailType, "subject", e.target.value)}
                        />
                      </Form.Group>

                      {/* Variable pills — NOW CONTEXTUAL */}
                      <VariablePills
                        availableVariables={VARIABLE_SETS[emailType] || []}
                        tokenHelp={VARIABLE_SETS[emailType]?.length ? "Use variables in the format ${variableName}" : "No variables available for this email type"}
                        onDragStart={(e, variable) => {
                          e.dataTransfer.setData("text/plain", variable.value);
                          e.dataTransfer.effectAllowed = "copy";
                        }}
                        onClick={(variable) => insertIntoQuill(emailType, variable.value)}
                      />

                      {/* Quill editor */}
                      <Form.Group>
                        <Form.Label>
                          <strong>Email Body:</strong>
                        </Form.Label>
                        <div onDrop={(e) => handleDropIntoQuill(e, emailType)} onDragOver={handleDragOverQuill}>
                          <ReactQuill
                            ref={quillRefs[emailType]}
                            theme="snow"
                            value={emailData[emailType].text}
                            onChange={(value) => updateEmailField(emailType, "text", value)}
                            modules={quillModules}
                            formats={quillFormats}
                            placeholder={`Enter your ${emailTypes[emailType].label.toLowerCase()} message. Drag a variable or click a pill to insert.`}
                            className="quill-editor"
                          />
                        </div>
                      </Form.Group>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>
            </div>
          ))}
        </Form>
      </Modal.Body>

      <Modal.Footer>
        <div className="d-flex justify-content-center align-items-center w-100">
          <Button variant="primary" className="px-5 py-2" onClick={handleSubmit}>
            {emailDetail ? "Save Changes" : "Create Email Templates"}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
}

export default AccountsEmailModal;

// ── VariablePills (updated to show help text) ──
function VariablePills({ availableVariables, onDragStart, onClick, tokenHelp }) {
  return (
    <div className="mb-3">
      <small className="text-muted d-block mb-2">
        <strong>Available Variables:</strong> {tokenHelp}
      </small>
      <div className="d-flex flex-wrap gap-2">
        {(availableVariables || []).map((variable, idx) => (
          <Badge
            key={idx}
            bg="secondary"
            className="user-select-none"
            style={{ cursor: "grab", padding: "6px 10px", fontSize: "0.8rem" }}
            draggable
            onDragStart={(e) => onDragStart(e, variable)}
            onClick={() => onClick(variable)}
            title="Drag to editor or click to insert"
          >
            {variable.label}
          </Badge>
        ))}
      </div>
    </div>
  );
}
